<template>
  <ReportCard :title="$t('report.transaction_volume_title')">
    <div class="ct-legend flex items-center justify-center gap-3">
      <span v-for="type in clientTypeKeys" :key="'vol-legend-' + type"
        class="flex items-center gap-1.5">
        <span class="w-2 h-2 rounded-full"
          :style="{ backgroundColor: clientTypeColor(type) }"></span>
        <span class="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
          {{ type }}
        </span>
      </span>
    </div>
    <div class="relative">
      <div ref="chartEl" class="w-full h-52"></div>
      <div v-if="loading"
        class="absolute inset-0 flex items-center justify-center bg-background/60 backdrop-blur-[1px] rounded-lg transition-opacity">
        <Loading />
      </div>
    </div>
  </ReportCard>
</template>

<script setup lang="ts">
import { computed, ref, shallowRef, watch, onBeforeUnmount, nextTick } from 'vue'
import * as echarts from 'echarts'
import type { SalesByClientTypesResponse, TaxDataKey } from '../../responses'
import { clientTypeColor } from '../../utils/ClientTypeColors'
import ReportCard from '../ReportCard.vue'
import Loading from '../Loading.vue'
import { useIsDarkMode } from '../../composables/useIsDarkMode'

const props = defineProps<{
  response: SalesByClientTypesResponse
  taxKey: TaxDataKey
  formatXLabel: (dateString: string) => string
  loading: boolean
}>()

const chartEl = ref<HTMLElement>()
const chartInstance = shallowRef<echarts.ECharts>()

/** Types with current-interval data */
const clientTypeKeys = computed<string[]>(() => {
  if (!props.response?.client_types) return []
  return Object.keys(props.response.client_types).filter(type => {
    const data = props.response.client_types[type]
    if (!data?.intervals?.length) return false
    return data.intervals.some(i =>
      i.data.period.tax_data.excluded_tax !== 0 || i.data.period.tax_data.included_tax !== 0,
    )
  })
})

const isDark = useIsDarkMode()

function getClientTypeDocIntervals(type: string): number[] {
  const data = props.response?.client_types[type]
  if (!data?.intervals) return []
  return data.intervals.map(i => i.data.period.total_documents)
}

function getClientTypeXAxis(): string[] {
  const firstType = clientTypeKeys.value[0]
  if (!firstType) return []
  const data = props.response?.client_types[firstType]
  if (!data?.intervals) return []
  return data.intervals.map(i => props.formatXLabel(i.date))
}

function buildVolumeChartOptions(): echarts.EChartsOption {
  const xAxisData = getClientTypeXAxis()
  const types = clientTypeKeys.value

  const rawByType: Record<string, number[]> = {}
  for (const type of types) {
    rawByType[type] = getClientTypeDocIntervals(type)
  }

  const intervalCount = xAxisData.length
  const totals: number[] = Array(intervalCount).fill(0)
  for (const type of types) {
    for (let i = 0; i < intervalCount; i++) {
      totals[i] += rawByType[type]?.[i] || 0
    }
  }

  const barSeries: echarts.BarSeriesOption[] = types.map((type, idx) => ({
    type: 'bar',
    name: type,
    stack: 'total',
    data: rawByType[type] || [],
    itemStyle: {
      color: clientTypeColor(type),
      borderRadius: idx === types.length - 1 ? [2, 2, 0, 0] : undefined,
    },
    emphasis: { focus: 'series' },
  }))

  return {
    grid: { left: '2%', right: '2%', bottom: '12%', top: '8%', containLabel: true },
    xAxis: {
      type: 'category',
      data: xAxisData,
      axisLine: { lineStyle: { color: isDark.value ? 'rgba(255,255,255,0.15)' : 'rgba(156,163,175,0.3)' } },
      axisTick: { show: false },
      axisLabel: { color: isDark.value ? 'rgba(255,255,255,0.6)' : 'rgba(75,85,99,0.8)', fontSize: 11 },
    },
    yAxis: {
      type: 'value',
      minInterval: 1,
      axisLine: { show: false },
      axisTick: { show: false },
      splitNumber: 4,
      splitLine: { lineStyle: { color: isDark.value ? 'rgba(255,255,255,0.08)' : 'rgba(229,231,235,0.4)', type: 'dashed' } },
      axisLabel: {
        color: isDark.value ? 'rgba(255,255,255,0.6)' : 'rgba(75,85,99,0.8)',
        fontSize: 11,
        formatter: (v: number) => String(Math.round(v)),
      },
    },
    tooltip: {
      trigger: 'axis',
      backgroundColor: isDark.value ? 'rgba(40,39,39,0.95)' : 'rgba(255,255,255,0.95)',
      borderColor: isDark.value ? 'rgba(255,255,255,0.1)' : 'rgba(229,231,235,0.8)',
      borderWidth: 1,
      borderRadius: 8,
      textStyle: { color: isDark.value ? 'rgba(255,255,255,0.9)' : 'rgba(17,24,39,0.9)', fontSize: 12 },
      confine: true,
      formatter: (params: any) => {
        if (!Array.isArray(params) || params.length === 0) return ''
        let html = `<div style="font-weight:600;margin-bottom:4px">${params[0].axisValue}</div>`
        let total = 0
        for (const p of params) {
          if (p.value == null) continue
          total += p.value
          html += `<div style="display:flex;align-items:center;gap:6px;margin-top:2px">`
          html += `${p.marker}<span>${p.seriesName}</span>`
          html += `<span style="margin-left:auto;font-weight:600">${p.value}</span>`
          html += `</div>`
        }
        html += `<div style="margin-top:4px;padding-top:4px;border-top:1px solid rgba(128,128,128,0.2);font-weight:600">Total: ${total}</div>`
        return html
      },
    },
    dataZoom: [{ type: 'inside', xAxisIndex: [0] }],
    series: barSeries,
  }
}

// --- Init / update / dispose ---
function initChart() {
  if (!chartEl.value) return
  chartInstance.value = echarts.init(chartEl.value)
  chartInstance.value.setOption(buildVolumeChartOptions())
}

function refreshChart() {
  if (chartInstance.value) {
    chartInstance.value.setOption(buildVolumeChartOptions(), true)
  } else {
    initChart()
  }
}

watch([clientTypeKeys, () => props.taxKey], () => {
  refreshChart()
}, { deep: true })

watch(() => props.response, async () => {
  await nextTick()
  refreshChart()
})

watch(isDark, () => refreshChart())

watch(chartEl, async (el) => {
  if (el && !chartInstance.value) {
    await nextTick()
    setTimeout(initChart, 300)
  }
}, { immediate: true })

onBeforeUnmount(() => {
  chartInstance.value?.dispose()
})

defineExpose({ refreshChart })
</script>
